/**
 * Racemarks Onboard uplink manifest reader (ra-gk3h).
 *
 * A boat running the Onboard uplink pushes its instrument bus to a Cloudflare
 * Worker, which appends immutable part objects to R2 and bumps ONE pointer doc
 * per vessel-day in Firestore:
 *
 *   R2        nmea/<COUNTRY>/<club>/<YYYY-MM-DD>/<vessel>/<HH>/<receive_ms>-<rand>.ndlog
 *   Firestore nmea_uplink_manifest/<COUNTRY>_<club>_<date>_<vessel>
 *
 * This module reads ONLY the pointer docs. It never touches R2 — which is the
 * point: the manifest carries part counts, byte totals, ms bounds and a
 * sentence-type histogram, so "is anything coming in, from which boat, when,
 * and does it even contain positions" is answerable for a whole club from a
 * single Firestore query, without opening one blob.
 *
 * The manifest deliberately carries NO write CODE (the worker's manifest.js
 * says so explicitly — the code is a capability), which is why it is safe to
 * expose to a club-scoped portal page at all.
 *
 * BEING REPLACED (ra-givfj, docs/design/onboard-manifest-identity.md): the
 * shapes above are what is deployed today. The manifest is re-keyed on
 * boat/date, and `club` and `country` are DELETED from the manifest and from
 * nmea_tokens alike — not renamed. `loadClubUplinkManifests` below queries
 * `club` and the `list` rule requires that constraint, so this page and the
 * rule move together (design §4.4): the club-scoped list becomes a callable
 * that starts from the club's registrations. Backfill BEFORE the switch (§7.2
 * step 2a) or the page loses rows it can no longer prove it may see.
 */
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';

/**
 * How recent `last_ms` must be for a vessel-day to count as still uplinking.
 *
 * The worker flushes in batches, so a boat that is genuinely transmitting still
 * goes quiet between flushes. This has to be comfortably longer than a flush
 * gap or an active boat flickers in and out of "live" on every poll.
 */
export const LIVE_WINDOW_MS = 15 * 60 * 1000;

/**
 * Sentence types that carry a position fix. Their presence is what decides
 * whether a track could be drawn from this vessel-day at all — wind/depth-only
 * uplinks are real and useful data, but they will never plot.
 */
export const POSITION_SENTENCES = ['RMC', 'GLL', 'GGA', 'GNS'];

/** Coerce to a finite number, or null. Firestore integers arrive as numbers,
 *  but a hand-repaired doc can carry a numeric string. */
function num(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Normalize one manifest doc into the shape the page renders.
 *
 * Every counter may be absent: the worker's field transforms only create
 * `first_ms`/`last_ms` when a flush carried parseable stamps, and a doc written
 * by an older worker has no `sentence_types` at all. Missing counters come back
 * as null (unknown), never 0 (measured zero) — the two mean different things on
 * a page whose whole job is saying what has and hasn't arrived.
 */
export function normalizeManifest(id, data) {
  const d = data || {};
  const sentenceTypes = {};
  for (const [k, v] of Object.entries(d.sentence_types || {})) {
    const n = num(v);
    if (n !== null) sentenceTypes[k] = n;
  }
  return {
    id,
    country: d.country || null,
    club: d.club || null,
    date: d.date || null,
    vessel: d.vessel || null,
    bucket: d.bucket || null,
    prefix: d.prefix || null,
    // Last-flush-wins, NOT a set — see `sourceIsAmbiguous`.
    source: d.source || null,
    partCount: num(d.part_count),
    bytes: num(d.bytes),
    firstMs: num(d.first_ms),
    lastMs: num(d.last_ms),
    updatedMs: num(d.updated_ms),
    sentenceTypes,
  };
}

/**
 * Total position fixes across every position-bearing sentence type, or null if
 * the doc carries no histogram at all (unknown, not zero).
 */
export function positionFixCount(sentenceTypes) {
  const types = sentenceTypes || {};
  if (Object.keys(types).length === 0) return null;
  let total = 0;
  for (const t of POSITION_SENTENCES) {
    const n = num(types[t]);
    if (n !== null) total += n;
  }
  return total;
}

/** True when this vessel-day contains at least one position fix, i.e. a track
 *  could be plotted from it. */
export function hasPositionData(sentenceTypes) {
  return (positionFixCount(sentenceTypes) || 0) > 0;
}

/** Sentence types as [type, count] pairs, busiest first. */
export function sentenceTypeRows(sentenceTypes) {
  return Object.entries(sentenceTypes || {})
    .filter(([, n]) => num(n) !== null)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/**
 * Whether a vessel-day is still receiving.
 *
 * A `lastMs` in the future counts as live rather than dead: the stamp is the
 * boat's own receive time and modest clock skew ahead of the viewer must not
 * read as "stopped hours ago".
 */
export function isLive(row, nowMs, windowMs = LIVE_WINDOW_MS) {
  const last = num(row && row.lastMs);
  if (last === null) return false;
  return nowMs - last <= windowMs;
}

/**
 * True when this vessel-day's `source` cannot be trusted to name every
 * contributor.
 *
 * The manifest doc id is (country, club, date, vessel) — it does NOT include
 * source — while `source` is re-set on every flush. So when a vessel carries
 * two uplink tokens (an instrument feed and an AIS feed both keyed to one hull,
 * as Tsunami IRL4007 does), BOTH write this same doc and the field shows
 * whichever flushed last. It is a hint, not an inventory, and the page must not
 * imply otherwise.
 *
 * Nothing in the manifest can detect this, so it is surfaced as a standing
 * caveat wherever `source` is shown rather than as a per-row flag.
 */
export const SOURCE_IS_LAST_FLUSH_ONLY = true;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** ISO date (YYYY-MM-DD) → "8 Aug 2026"; passes through anything unexpected. */
export function formatDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return iso || '';
  const [, y, mo, d] = m;
  return `${parseInt(d, 10)} ${MONTHS[parseInt(mo, 10) - 1]} ${y}`;
}

/**
 * Epoch ms → "HH:MM" in UTC.
 *
 * UTC, not local: the vessel-day the row belongs to is itself a UTC day (the
 * worker's key scheme uses `toISOString().slice(0,10)`), so rendering the span
 * in local time would put times outside the date they are filed under. Callers
 * label the unit — an unlabelled clock is the bug this avoids.
 */
export function formatUtcClock(ms) {
  const n = num(ms);
  if (n === null) return null;
  const d = new Date(n);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** "12:17–15:03 UTC" for a vessel-day's ms bounds, or null if neither is known. */
export function formatUtcSpan(firstMs, lastMs) {
  const a = formatUtcClock(firstMs);
  const b = formatUtcClock(lastMs);
  if (a === null && b === null) return null;
  return `${a ?? '?'}–${b ?? '?'} UTC`;
}

/**
 * How long ago a vessel-day last flushed, in words — or null once the answer is
 * "over a day", because the Date column already says which day it was and
 * "12804 min ago" is not an answer anyone can read.
 */
export function formatAgo(lastMs, nowMs) {
  const last = num(lastMs);
  if (last === null) return null;
  // Floor, not round: a 30-second-old flush is "just now", not "1 min ago" —
  // rounding up would report a boat as staler than it is.
  const mins = Math.floor((nowMs - last) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return null;
}

/**
 * Bytes → a compact human size. Null in, null out (unknown ≠ "0 B").
 *
 * Precision tapers with magnitude so a size stays both short and honest: 9.15 MB
 * keeps the digits that distinguish one race from another, 30.4 MB doesn't
 * round away to a bare "30", and a 4-figure size doesn't carry noise decimals.
 */
export function formatBytes(bytes) {
  const n = num(bytes);
  if (n === null) return null;
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const dp = v >= 100 ? 0 : v >= 10 ? 1 : 2;
  return `${v.toFixed(dp)} ${units[i]}`;
}

/** Newest day first, then vessel A→Z for a stable order within a day.
 *  Returns a new array (does not mutate the input). */
export function sortVesselDaysDesc(rows) {
  return [...rows].sort((a, b) => {
    const byDate = (b.date || '').localeCompare(a.date || '');
    if (byDate !== 0) return byDate;
    return (a.vessel || '').localeCompare(b.vessel || '');
  });
}

function toRows(snap) {
  return sortVesselDaysDesc(snap.docs.map((d) => normalizeManifest(d.id, d.data())));
}

/**
 * Every uplink vessel-day for one club, newest first.
 *
 * Equality-only on `club`, sorted client-side. That is deliberate and load-
 * bearing, not laziness: adding `orderBy` would demand the composite
 * (country, club, date, vessel) index, which is declared in
 * firebase/firestore.indexes.json but is only deployed to prod off release/*.
 * The analysis-side reader learned this the hard way — every ops regen answered
 * a bare HTTP 400 for every club-day until the sort moved out of the query
 * (see analysis/race_reports/collectors/nmea_uplink.list_manifests). Discovery
 * must not be hostage to an index-deploy cadence. Keep the sort here.
 *
 * The club constraint is also what makes the read legal: firestore.rules gates
 * `list` on the query proving every result belongs to the caller's own club.
 */
export async function loadClubUplinkManifests(clubId) {
  const q = query(collection(db, 'nmea_uplink_manifest'), where('club', '==', clubId));
  return toRows(await getDocs(q));
}

/**
 * Every uplink vessel-day across every club, newest first. Global admin only —
 * firestore.rules rejects an unconstrained list for anyone else.
 *
 * Worth having rather than a curiosity: club keys have not been consistent over
 * time (Black Sheep's July manifests are filed under `RCYC`, its August ones
 * under `ie-dublin-bay`), so a club-filtered view provably cannot show every
 * boat that ever uplinked. Without this, the page would under-report silently.
 */
export async function loadAllUplinkManifests() {
  return toRows(await getDocs(collection(db, 'nmea_uplink_manifest')));
}
